import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { toast } from "sonner";
import { Plus, ArrowLeft, Megaphone, FileText } from "@phosphor-icons/react";

const DOC_TYPES = [
  { value: "projeto_apresentado", label: "Projeto apresentado" },
  { value: "projeto_aprovado", label: "Projeto aprovado" },
  { value: "proposta_andamento", label: "Proposta em andamento" },
  { value: "votacao", label: "Votação / posicionamento" },
  { value: "posicionamento", label: "Posicionamento oficial" },
  { value: "discurso", label: "Discurso" },
  { value: "agenda_publica", label: "Agenda pública / evento" },
  { value: "acao_mandato", label: "Ação do mandato" },
  { value: "entrega", label: "Entrega realizada" },
  { value: "recurso_destinado", label: "Recurso destinado" },
  { value: "emenda", label: "Emenda" },
  { value: "obra", label: "Obra" },
  { value: "reuniao", label: "Reunião" },
  { value: "noticia", label: "Notícia / clipping" },
  { value: "documento", label: "Documento" },
  { value: "faq", label: "FAQ / Pergunta frequente" },
  { value: "servico_gabinete", label: "Serviço prestado pelo gabinete" },
  { value: "canal_oficial", label: "Canal oficial" },
  { value: "info_partido_instituicao", label: "Informação institucional" },
  { value: "outro", label: "Outro" },
];

/**
 * MandateKnowledge — base de conhecimento isolada do representante.
 *   • Lista documentos indexados (texto manual + futura ingestão de PDF).
 *   • Form inline para subir novo item com type + fonte.
 *   • Botão "Marcar como atualização factual" dispara o funcionário
 *     proativo (mandate_topic_tracker) que avisa os cidadãos opt-in.
 */
export default function MandateKnowledge() {
  const { mandateId } = useParams();
  const [rep, setRep] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "", body: "", doc_type: "projeto_apresentado",
    source_url: "", occurred_at: "",
  });
  const [showEvent, setShowEvent] = useState(null);  // {topic, status_text, source, document_id}

  async function reload() {
    setLoading(true);
    try {
      const [{ data: repData }, { data: kbData }] = await Promise.all([
        api.get(`/mandate/representatives/${mandateId}`),
        api.get(`/mandate/representatives/${mandateId}/kb`),
      ]);
      setRep(repData);
      setDocs(kbData?.documents || []);
    } catch {
      toast.error("Não foi possível carregar a base.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [mandateId]);

  async function submitDoc() {
    if (!form.title || !form.body || form.body.length < 10) {
      toast.error("Preencha título e conteúdo (mínimo 10 caracteres).");
      return;
    }
    try {
      await api.post(`/mandate/representatives/${mandateId}/kb`, form);
      toast.success("Conteúdo adicionado à base.");
      setShowForm(false);
      setForm({ ...form, title: "", body: "", source_url: "", occurred_at: "" });
      await reload();
    } catch {
      toast.error("Falha ao salvar.");
    }
  }

  async function markAsEvent(docId, topicDefault, statusDefault) {
    setShowEvent({
      topic: topicDefault || "",
      status_text: statusDefault || "",
      source: "",
      document_id: docId,
    });
  }

  async function submitEvent() {
    if (!showEvent?.topic || !showEvent?.status_text) {
      toast.error("Informe o tema e o status factual.");
      return;
    }
    try {
      await api.post(`/mandate/kb/events`, {
        mandate_id: mandateId,
        topic: showEvent.topic,
        status_text: showEvent.status_text,
        source: showEvent.source || "Base oficial do mandato",
        document_id: showEvent.document_id,
      });
      toast.success("Atualização enfileirada. Cidadãos interessados serão notificados pelo Coordenador.");
      setShowEvent(null);
    } catch {
      toast.error("Falha ao registrar atualização.");
    }
  }

  return (
    <div className="space-y-6" data-testid="mandate-knowledge-page">
      <Topbar
        title={rep ? `Base: ${rep.name}` : "Base de conhecimento"}
        subtitle="Conteúdo que o Agentus usa para responder sobre este representante. Sem fonte, sem resposta."
      />
      <div className="flex items-center justify-between">
        <Link
          to="/app/mandato/representantes"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={14} /> Voltar aos representantes
        </Link>
        <button
          onClick={() => setShowForm(true)}
          data-testid="mandate-kb-new-btn"
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus size={16} weight="bold" /> Novo item
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Carregando...</div>
      ) : docs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <FileText size={32} className="mx-auto mb-3 text-slate-400" />
          <p className="text-slate-800 font-medium">Base vazia.</p>
          <p className="text-sm text-slate-500 mt-1">Suba projetos, votações, agenda, discursos ou entregas desse representante. O Agentus só responde com base no que estiver aqui.</p>
        </div>
      ) : (
        <ul className="divide-y rounded-xl border border-slate-200 bg-white">
          {docs.map((d) => {
            const dtype = (d.tags || []).find((t) => t.startsWith("mandate_type:"))?.replace("mandate_type:", "") || "outro";
            const label = DOC_TYPES.find((o) => o.value === dtype)?.label || dtype;
            return (
              <li key={d.id} className="p-4" data-testid={`mandate-kb-item-${d.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-500 uppercase tracking-wide">{label}</div>
                    <div className="text-slate-900 font-medium truncate">{d.title}</div>
                    {d.meta?.occurred_at ? (
                      <div className="text-xs text-slate-500 mt-0.5">{d.meta.occurred_at}</div>
                    ) : null}
                    {d.meta?.source_url ? (
                      <a href={d.meta.source_url} target="_blank" rel="noreferrer"
                         className="text-xs text-blue-700 hover:underline mt-1 inline-block">
                        {d.meta.source_url}
                      </a>
                    ) : null}
                  </div>
                  <button
                    onClick={() => markAsEvent(d.id, d.title, "")}
                    data-testid={`mandate-kb-mark-event-${d.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <Megaphone size={14} /> Marcar como atualização
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Modal novo item */}
      {showForm ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-xl bg-white p-6 space-y-3" data-testid="mandate-kb-form-modal">
            <h3 className="text-lg font-semibold text-slate-900">Novo item na base</h3>
            <select
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              value={form.doc_type}
              onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
              data-testid="mandate-kb-doctype-select"
            >
              {DOC_TYPES.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
            <input
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Título / assunto"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="mandate-kb-title-input"
            />
            <textarea
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              rows={6}
              placeholder="Conteúdo factual (projeto, ementa, trecho de discurso, nota, ata, etc.)"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="mandate-kb-body-input"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                className="rounded border border-slate-300 px-3 py-2 text-sm"
                placeholder="Fonte / link oficial (opcional)"
                value={form.source_url}
                onChange={(e) => setForm({ ...form, source_url: e.target.value })}
              />
              <input
                className="rounded border border-slate-300 px-3 py-2 text-sm"
                placeholder="Data (AAAA-MM-DD, opcional)"
                value={form.occurred_at}
                onChange={(e) => setForm({ ...form, occurred_at: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setShowForm(false)} className="rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
              <button onClick={submitDoc} data-testid="mandate-kb-save-btn" className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">Salvar</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modal marcar como atualização factual */}
      {showEvent ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 space-y-3" data-testid="mandate-kb-event-modal">
            <h3 className="text-lg font-semibold text-slate-900">Marcar como atualização factual</h3>
            <p className="text-sm text-slate-500">
              O Coordenador vai avisar proativamente apenas os cidadãos que
              deram opt-in para acompanhar esse tema, respeitando opt-out,
              horário e frequência.
            </p>
            <input
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Tema (ex: ampliação da UBS Centro)"
              value={showEvent.topic}
              onChange={(e) => setShowEvent({ ...showEvent, topic: e.target.value })}
            />
            <textarea
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              rows={3}
              placeholder="Status factual (o que avançou de concreto)"
              value={showEvent.status_text}
              onChange={(e) => setShowEvent({ ...showEvent, status_text: e.target.value })}
            />
            <input
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Fonte citável (ex: Diário Oficial, Ata nº...)"
              value={showEvent.source}
              onChange={(e) => setShowEvent({ ...showEvent, source: e.target.value })}
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setShowEvent(null)} className="rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
              <button onClick={submitEvent} className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">Notificar cidadãos interessados</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
